import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { matchPath } from 'react-router-dom'
import { useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import SiteTakeoverBackgroundImage from './SiteTakeoverBackgroundImage'
import { useLabFeatureFlag } from 'hooks/FeatureFlag/useLabFeatureFlag'
import { LabFeatureFlags } from 'constants/lab'
import { isLabTakeoverBannerContentPayload } from 'lib/lab/dynamicPayload'
import BodyText from 'LuxKit/Typography/BodyText'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { isBannerDismissed, recordSiteBannerDismissal } from 'storage/siteBanner'
import { offerPageRoutes } from 'takeovers/utils/constants/takeoverRoutes'
import moment from 'moment'
import { stripMarkdownFormatting } from 'lib/string/stripMarkdown'
import useResponsiveSelector from 'hooks/useResponsiveSelector'
import { isBannerHiddenForPath } from 'lib/routing/routeVisibility'
import { LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN } from 'luxPlus/constants/base'
import { useSiteTakeoverTheme } from 'takeovers/utils/hooks/useSiteTakeoverTheme'
import styled from 'styled-components'
import { rem } from 'polished'
import type { PageBannerCarouselItem } from 'LuxKit/Banners/types'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'

const Background = styled(SiteTakeoverBackgroundImage)`
  position: absolute;
  inset: 0;
  width: 100%;
`

const TitleSpacer = styled.span`
  margin-inline-end: ${rem(12)};
`

export const homePageRoutes: Array<string> = [
  '/:regionCode',
]

const toursRoutes: Array<string> = [
  '/:regionCode/tours',
  '/:regionCode/tour/:offerId',
]

const BANNER_DISMISSAL_PERIOD_DAYS = 7

function matchesAnyRoute(pathname: string, paths: Array<string>, end = false) {
  return paths.some(path => matchPath({ path, end }, pathname))
}

function useSiteTakeoverBannerItem(): PageBannerCarouselItem | null {
  const intl = useIntl()
  const currentPath = useAppSelector(state => state.router.location.pathname)

  const shouldShowGenericBanner = useShouldShowBanner({ location: 'generic' })
  const siteTakeover = useCurrentSiteTakeover()
  const siteTakeoverOnTour = useCurrentSiteTakeover({ heroVertical: 'tours' })

  const labSiteTakeoverTheme = useSiteTakeoverTheme()

  const hide = useMemo((): boolean => {
    // honour custom config excepted routes
    const exceptedRoutes: Array<string> = siteTakeover?.siteWide?.banner?.exceptedRoutes ?? EmptyArray
    // honour custom config to show on home page
    const showOnHomePage = siteTakeover?.siteWide?.banner?.showOnHomePage ?? false
    // hide on:
    // - The home page (has its own carousel takeover) - unless showOnHomePage is true
    // - Search pages (has its own banner)
    // - Checkout flow
    // - Booking flow
    // - Custom takeover excepted routes (e.g. lux-plus page)
    // - Tours routes when a tours-specific takeover is active (avoid banner + carousel at the same time)
    return (!showOnHomePage && matchesAnyRoute(currentPath, homePageRoutes, true)) ||
      isBannerHiddenForPath(currentPath, 'siteTakeoverPageBanner') ||
      (!!exceptedRoutes.length && matchesAnyRoute(currentPath, exceptedRoutes)) ||
      (!!siteTakeoverOnTour && matchesAnyRoute(currentPath, toursRoutes))
  }, [currentPath, siteTakeover, siteTakeoverOnTour])

  const enableSiteTakeoverBannerContent = useLabFeatureFlag(LabFeatureFlags.enableTakeoverBannerContent)
  const siteTakeoverBannerContentPayload: App.LabTakeoverBannerContentPayload | undefined = useMemo(() => {
    if (enableSiteTakeoverBannerContent?.enabled) {
      const payload = enableSiteTakeoverBannerContent.value
      if (isLabTakeoverBannerContentPayload(payload)) {
        return payload
      }
    }
    return undefined
  }, [enableSiteTakeoverBannerContent])

  const isPopupType = siteTakeoverBannerContentPayload?.genericBannerType === 'takeoverpopup' ||
    siteTakeoverBannerContentPayload?.genericBannerType === 'whitepopup'
  const useLab = !!siteTakeoverBannerContentPayload && !isPopupType
  const useCustom = !useLab && !!siteTakeover?.siteWide?.banner

  const bannerIdentifier = useMemo(() => {
    if (siteTakeoverBannerContentPayload) {
      return `siteTakeoverBanner_lab_${siteTakeoverBannerContentPayload.title ?? 'default'}`
    }
    if (siteTakeover?.siteWide?.banner) {
      return `siteTakeoverBanner_custom_${siteTakeover.id ?? 'default'}`
    }
    return 'siteTakeoverBanner_default'
  }, [siteTakeoverBannerContentPayload, siteTakeover])

  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    setIsDismissed(isBannerDismissed(bannerIdentifier))
  }, [bannerIdentifier])

  const onDismissHandler = useCallback(() => {
    setIsDismissed(true)
    recordSiteBannerDismissal(bannerIdentifier, moment().add(BANNER_DISMISSAL_PERIOD_DAYS, 'days').toISOString())
  }, [bannerIdentifier])

  const shouldHideOnMobile = useMemo(() => {
    return !!siteTakeover?.siteWide?.banner?.hideOnOfferPagesForMobileScreenSize &&
      matchesAnyRoute(currentPath, offerPageRoutes)
  }, [siteTakeover, currentPath])

  const isDesktop = useResponsiveSelector({
    mobile: false,
  }) ?? true
  const locationIcons = siteTakeoverBannerContentPayload?.locationIcons
  const logoConfig = useMemo<App.SiteTakeoverImageConfig | undefined>(() => {
    const locationIcon = locationIcons?.generic
    if (locationIcon) {
      const icon = isDesktop ? locationIcon.desktopIcon : locationIcon.mobileIcon
      if (icon) {
        const iconWidth = isDesktop ? locationIcon.desktopIconDimension?.width : locationIcon.mobileIconDimension?.width
        const iconHeight = isDesktop ? locationIcon.desktopIconDimension?.height : locationIcon.mobileIconDimension?.height
        return {
          id: icon,
          width: iconWidth,
          height: iconHeight,
        }
      }
    }
    const fallbackLogoRef = labSiteTakeoverTheme?.logoRef
    return fallbackLogoRef ? { id: fallbackLogoRef } : undefined
  }, [locationIcons, isDesktop, labSiteTakeoverTheme?.logoRef])

  const { SHOULD_OVERRIDE_LAB_CAMPAIGN } = LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN

  return useMemo(() => {
    if (!shouldShowGenericBanner || hide || isDismissed || shouldHideOnMobile || SHOULD_OVERRIDE_LAB_CAMPAIGN) {
      return null
    }

    if (!useLab && !useCustom) return null

    const title = siteTakeoverBannerContentPayload?.title ? stripMarkdownFormatting(siteTakeoverBannerContentPayload.title) : undefined
    const subtitle = siteTakeoverBannerContentPayload?.subtitle
      ? stripMarkdownFormatting(siteTakeoverBannerContentPayload.subtitle)
      : undefined
    const ctaLink = siteTakeoverBannerContentPayload?.ctaLink
    const backgroundStyle = siteTakeoverBannerContentPayload?.backgroundStyle
    const inversePalette = siteTakeoverBannerContentPayload?.palette

    const viewAllText = intl.formatMessage({
      id: 'oQ3XGGiARCmD2MzbKTM7',
      defaultMessage: 'View all',
      description: 'CTA link text to view all takeover campaign offers on page banner',
    })

    const bannerContent = useLab ? (
      <>
        <CSSBreakpoint max="mobile">
          <Group direction="horizontal" gap={8} verticalAlign="center">
            {logoConfig && <Image id={logoConfig.id} dpr={2} enhancedFormats={false} width={logoConfig.width && logoConfig.height ? logoConfig.width : 32} height={logoConfig.width && logoConfig.height ? logoConfig.height : 32} />}
            <Group direction="vertical" verticalAlign="start">
              <BodyText variant="medium" weight="bold">
                {title}
              </BodyText>
              <BodyText variant="small">
                {subtitle}
              </BodyText>
            </Group>
          </Group>
        </CSSBreakpoint>
        <CSSBreakpoint min="tablet">
          <Group direction="horizontal" gap={12} verticalAlign="center">
            {logoConfig && <Image id={logoConfig.id} dpr={2} enhancedFormats={false} width={logoConfig.width && logoConfig.height ? logoConfig.width : 32} height={logoConfig.width && logoConfig.height ? logoConfig.height : 32} />}
            <BodyText variant="medium">
              <b>{title}</b>
              <TitleSpacer />
              {subtitle}
            </BodyText>
          </Group>
        </CSSBreakpoint>
      </>
    ) : siteTakeover?.siteWide?.banner.description

    let customBackground: React.ReactNode | undefined
    if (useCustom) {
      customBackground = <Background background="default" placement="siteWideBanner" />
    }

    let style: PageBannerCarouselItem['style'] = undefined
    if (useLab && backgroundStyle) {
      style = {
        background: backgroundStyle,
      }
    }

    const customBanner = siteTakeover?.siteWide?.banner
    const bannerAction = customBanner?.bannerAction
    let hideMobileActionButton: boolean | undefined
    let bannerActionButtonProps: Omit<NonNullable<typeof bannerAction>, 'hideMobileActionButton'> | undefined
    if (bannerAction) {
      ({
        hideMobileActionButton,
        ...bannerActionButtonProps
      } = bannerAction)
    }
    const bannerPalette = customBanner?.palette ?? siteTakeover?.palette
    const bannerButtonVariant: NonNullable<PageBannerCarouselItem['actionButtonProps']>['variant'] = bannerPalette === 'inverse' ? 'ghost' : 'default'

    let actionButtonProps: PageBannerCarouselItem['actionButtonProps']
    if (bannerActionButtonProps) {
      actionButtonProps = {
        kind: 'primary',
        ...bannerActionButtonProps,
        size: bannerActionButtonProps.size ?? 'small',
        variant: bannerButtonVariant,
      }
    }
    const pageBannerProps = useCustom && siteTakeover?.siteWide?.banner ? {
      actionButtonProps,
      icon: siteTakeover.siteWide.banner.logo,
    } : {}

    return {
      id: bannerIdentifier,
      kind: inversePalette === 'inverse' ? 'none-inverse' : 'none',
      onDismiss: onDismissHandler,
      children: bannerContent,
      style,
      customBackground,
      mobileImageId: useCustom ? siteTakeover?.imageId : undefined,
      textLinkProps: ctaLink ? {
        to: ctaLink,
        children: viewAllText,
      } : undefined,
      hideMobileActionButton,
      ...pageBannerProps,
    }
  }, [shouldShowGenericBanner, hide, isDismissed, shouldHideOnMobile, SHOULD_OVERRIDE_LAB_CAMPAIGN, useLab, useCustom, siteTakeoverBannerContentPayload, siteTakeover, logoConfig, intl, bannerIdentifier, onDismissHandler])
}

export default useSiteTakeoverBannerItem
